import React, { useState, useEffect } from "react";
import PhoneInput from "../../../common/components/PhoneInput";
import { updateUserProfile } from "../../services/adminUserService";
import { useToast } from "../../../common/toast/ToastContext";
import axios from "axios";

const LEAD_SOURCES = ["ORGANIC", "REFERRAL", "PUBLIC_LINK"];
const PLATFORMS = ["WEBSITE", "ANDROID"];

const overlay = {
  position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
  background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center",
  justifyContent: "center", zIndex: 1000,
};

const modal = {
  background: "#fff", padding: "24px", borderRadius: "8px",
  width: "480px", maxHeight: "90vh", overflowY: "auto",
};

const fieldStyle = { marginBottom: "14px" };
const labelStyle = { display: "block", marginBottom: "4px", fontWeight: 500, fontSize: "13px" };
const inputStyle = { width: "100%", padding: "8px", boxSizing: "border-box", borderRadius: "4px", border: "1px solid #ccc" };
const selectStyle = { ...inputStyle };
const toggleRow = { display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" };

const EditUserProfileModal = ({ user, onClose, onSuccess }) => {
  const { showToast } = useToast();
  const [countryCodes, setCountryCodes] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    timeZone: user.timeZone || "",
    mobileNumber: user.mobileNumber || "",
    countryCode: user.countryCode || "+91",
    whatsappEnabled: user.whatsappConsent || false,
    profileComplete: user.profileComplete || false,
    leadSource: user.leadSource || "",
    campaignId: user.campaignId || "",
    registrationPlatform: user.registrationPlatform || "",
  });

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BASE_URL}/public/country-codes`)
      .then(res => setCountryCodes(res.data?.data || []))
      .catch(() => {});
  }, []);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    setError("");
    const orig = {
      name: user.name || "",
      email: user.email || "",
      timeZone: user.timeZone || "",
      mobileNumber: user.mobileNumber || "",
      countryCode: user.countryCode || "+91",
      whatsappEnabled: user.whatsappConsent || false,
      profileComplete: user.profileComplete || false,
      leadSource: user.leadSource || "",
      campaignId: user.campaignId || "",
      registrationPlatform: user.registrationPlatform || "",
    };

    const payload = {};
    Object.keys(form).forEach(key => {
      if (form[key] !== orig[key]) payload[key] = form[key];
    });

    // mobileNumber and countryCode must go together
    if ("mobileNumber" in payload || "countryCode" in payload) {
      payload.mobileNumber = form.mobileNumber;
      payload.countryCode = form.countryCode;
    }

    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      const { data } = await updateUserProfile(user.id, payload);
      showToast(data.message || "User profile updated successfully", "success");
      onSuccess(data.response);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        <h3 style={{ marginTop: 0 }}>Edit User Profile</h3>

        <div style={fieldStyle}>
          <label style={labelStyle}>Name</label>
          <input style={inputStyle} value={form.name} onChange={e => set("name", e.target.value)} />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Email</label>
          <input style={inputStyle} type="email" value={form.email} onChange={e => set("email", e.target.value)} />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Time Zone</label>
          <input style={inputStyle} value={form.timeZone} onChange={e => set("timeZone", e.target.value)} placeholder="e.g. Asia/Kolkata" />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Mobile Number</label>
          <PhoneInput
            countryCodes={countryCodes}
            dialCode={form.countryCode}
            onDialCodeChange={val => set("countryCode", val)}
            mobileNumber={form.mobileNumber}
            onMobileNumberChange={val => set("mobileNumber", val)}
            placeholder="9876543210"
          />
        </div>

        <div style={toggleRow}>
          <input type="checkbox" id="whatsappEnabled" checked={form.whatsappEnabled}
            onChange={e => set("whatsappEnabled", e.target.checked)} />
          <label htmlFor="whatsappEnabled">WhatsApp Enabled</label>
        </div>

        <div style={toggleRow}>
          <input type="checkbox" id="profileComplete" checked={form.profileComplete}
            onChange={e => set("profileComplete", e.target.checked)} />
          <label htmlFor="profileComplete">Profile Complete</label>
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Lead Source</label>
          <select style={selectStyle} value={form.leadSource} onChange={e => set("leadSource", e.target.value)}>
            <option value="">— Select —</option>
            {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Campaign ID</label>
          <input style={inputStyle} value={form.campaignId} onChange={e => set("campaignId", e.target.value)} placeholder="e.g. CAMP_2025" />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Registration Platform</label>
          <select style={selectStyle} value={form.registrationPlatform} onChange={e => set("registrationPlatform", e.target.value)}>
            <option value="">— Select —</option>
            {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        {error && (
          <p style={{ color: "#dc2626", fontSize: "13px", marginBottom: "12px" }}>{error}</p>
        )}

        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button onClick={onClose} disabled={saving}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUserProfileModal;
